<script setup lang="ts">
/**
 * Floating pill for running timers: a pulsing dot, the clock of the longest
 * timer and one line under it — that timer's label, or with several timers
 * „2 parallel · gesamt 1:23:45“. Clicking the pill emits `open` (e.g. the
 * time-tracking window), the stop button emits `stop` with the longest timer.
 *
 * The pill only DISPLAYS the caller's `timers`: starting, pausing, stopping
 * and booking stay in the caller's timer model. It never positions itself —
 * place it where it belongs, e.g. `position: fixed` at the bottom left with
 * `z-index: var(--nxt-z-float)`. Without timers it renders nothing. The dot
 * pulses while a timer runs and holds still under reduced motion.
 */
import { computed } from 'vue';
import {
  elapsedSeconds,
  formatElapsed,
  toEpochMs,
  useSecondTicker,
  type NxTimestamp,
} from './elapsed';
import NxTabularNumber from '../TabularNumber/NxTabularNumber.vue';

/** One timer as the pill sees it. */
export interface NxTimerPillTimer {
  id: string | number;
  /** What the timer runs for, e.g. „A-2026-0412 · Datenübernahme Barth“. */
  label: string;
  /** Start of the current running segment. */
  startedAt?: NxTimestamp | null;
  /** Time banked from earlier segments, in seconds. */
  accumulatedSeconds?: number;
  /** `false` for a paused timer. Defaults to `true`. */
  running?: boolean;
}

const props = withDefaults(
  defineProps<{
    timers: readonly NxTimerPillTimer[];
    /** Second line with more than one timer. */
    parallelLabel?: (count: number, total: string) => string;
    /** Tooltip of the pill, says what a click does. */
    openLabel?: string;
    /** Text of the stop button; empty hides the button. */
    stopLabel?: string;
    /** Accessible name of the whole pill. */
    ariaLabel?: string;
    /** „01:02:03“ instead of „1:02:03“. */
    padHours?: boolean;
  }>(),
  {
    parallelLabel: (count: number, total: string) => `${count} parallel · gesamt ${total}`,
    openLabel: 'Zeiterfassung öffnen',
    stopLabel: 'Stopp & erfassen',
    ariaLabel: 'Zeiterfassung',
    padHours: false,
  }
);

const emit = defineEmits<{
  open: [];
  stop: [timer: NxTimerPillTimer];
}>();

function isRunning(timer: NxTimerPillTimer): boolean {
  return timer.running !== false && toEpochMs(timer.startedAt) != null;
}

const ticking = computed(() => props.timers.some(isRunning));

// Tick on the seconds of the earliest running start: that is the longest timer.
const anchor = computed(() => {
  const starts = props.timers
    .filter(isRunning)
    .map((timer) => toEpochMs(timer.startedAt) as number);
  return starts.length ? Math.min(...starts) : null;
});
const now = useSecondTicker(ticking, () => anchor.value);

const clocks = computed(() =>
  props.timers.map((timer) => ({
    timer,
    seconds: elapsedSeconds(
      timer.startedAt,
      timer.accumulatedSeconds ?? 0,
      now.value,
      timer.running !== false
    ),
  }))
);

const longest = computed(() =>
  clocks.value.reduce<(typeof clocks.value)[number] | null>(
    (best, clock) => (!best || clock.seconds > best.seconds ? clock : best),
    null
  )
);

const time = computed(() =>
  formatElapsed(longest.value?.seconds ?? 0, { padHours: props.padHours })
);

const detail = computed(() => {
  if (clocks.value.length === 1) return clocks.value[0].timer.label;
  const total = clocks.value.reduce((sum, clock) => sum + clock.seconds, 0);
  return props.parallelLabel(
    clocks.value.length,
    formatElapsed(total, { padHours: props.padHours })
  );
});

function stop() {
  if (longest.value) emit('stop', longest.value.timer);
}
</script>

<template>
  <div
    v-if="timers.length"
    class="nx-timer-pill"
    :class="{ 'is-running': ticking }"
    role="group"
    :aria-label="ariaLabel"
  >
    <button type="button" class="nx-timer-pill__open" :title="openLabel" @click="emit('open')">
      <span class="nx-timer-pill__dot" aria-hidden="true" />
      <span class="nx-timer-pill__text">
        <NxTabularNumber class="nx-timer-pill__time" :value="time" />
        <NxTabularNumber class="nx-timer-pill__detail" :value="detail" />
      </span>
    </button>
    <button v-if="stopLabel" type="button" class="nx-timer-pill__stop" @click="stop">
      {{ stopLabel }}
    </button>
  </div>
</template>

<style scoped>
/* An inverted chrome surface (the nav colours), so it stands out over any page. */
.nx-timer-pill {
  display: inline-flex;
  align-items: center;
  gap: var(--nxt-space-2xs);
  max-width: 100%;
  padding: var(--nxt-space-2xs);
  border: var(--nxt-hairline) solid color-mix(in srgb, var(--nxt-nav-fg) 14%, transparent);
  border-radius: var(--nxt-radius-full);
  background: var(--nxt-nav-bg);
  color: var(--nxt-nav-fg);
  box-shadow: var(--nxt-shadow-md);
  font-size: var(--nxt-font-size-sm);
}

.nx-timer-pill__open,
.nx-timer-pill__stop {
  border: 0;
  border-radius: var(--nxt-radius-full);
  background: transparent;
  color: inherit;
  font: inherit;
  cursor: pointer;
  transition: background-color var(--nxt-motion-fast) var(--nxt-ease-out);
}
.nx-timer-pill__open:focus-visible,
.nx-timer-pill__stop:focus-visible {
  outline: calc(var(--nxt-hairline) * 2) solid var(--nxt-focus-ring);
  outline-offset: var(--nxt-hairline);
}

.nx-timer-pill__open {
  display: inline-flex;
  align-items: center;
  gap: var(--nxt-space-sm);
  min-width: 0;
  min-height: var(--nxt-control-size-lg);
  padding: var(--nxt-space-2xs) var(--nxt-space-sm);
  text-align: start;
}
.nx-timer-pill__open:hover {
  background: color-mix(in srgb, var(--nxt-nav-fg) 8%, transparent);
}

.nx-timer-pill__dot {
  flex: none;
  width: var(--nxt-dot-size-lg);
  height: var(--nxt-dot-size-lg);
  border-radius: var(--nxt-radius-full);
  background: var(--nxt-nav-fg-muted);
}
.nx-timer-pill.is-running .nx-timer-pill__dot {
  background: var(--nxt-success-bg);
  animation: nx-timer-pill-pulse calc(var(--nxt-motion-slow) * 4) var(--nxt-ease-in-out) infinite;
}
@keyframes nx-timer-pill-pulse {
  50% {
    opacity: 0.35;
  }
}

.nx-timer-pill__text {
  display: flex;
  flex-direction: column;
  min-width: 0;
  line-height: var(--nxt-line-height-tight);
}
.nx-timer-pill__time {
  font-size: var(--nxt-font-size-md);
  font-weight: var(--nxt-font-weight-bold);
  font-variant-numeric: tabular-nums;
}
.nx-timer-pill__detail {
  max-width: var(--nxt-panel-width-md);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: var(--nxt-font-size-xs);
  color: var(--nxt-nav-fg-muted);
}

.nx-timer-pill__stop {
  flex: none;
  min-height: var(--nxt-control-size-sm);
  padding: var(--nxt-space-2xs) var(--nxt-space-sm);
  background: color-mix(in srgb, var(--nxt-nav-fg) 16%, transparent);
  font-size: var(--nxt-font-size-xs);
  font-weight: var(--nxt-font-weight-semibold);
  white-space: nowrap;
}
.nx-timer-pill__stop:hover {
  background: color-mix(in srgb, var(--nxt-nav-fg) 26%, transparent);
}

@media (prefers-reduced-motion: reduce) {
  .nx-timer-pill.is-running .nx-timer-pill__dot {
    animation: none;
  }
  .nx-timer-pill__open,
  .nx-timer-pill__stop {
    transition: none;
  }
}
@media (forced-colors: active) {
  .nx-timer-pill {
    border-color: CanvasText;
  }
}
</style>
